import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import Spinner from "./Spinner";
import { useTemplateComponentClassNames } from "../store/template/template-style-context";

const mergeClassNames = (...classNames) => classNames.filter(Boolean).join(" ");

const Pagination = ({
  onPageClick,
  total,
  limit,
  page,
  isFetching,
  changeLimit = false,
  onLimitChange,
  alwayShowPagination = true,
  isLoading,
  enableManualMode = false,
  autoLoadThreshold = 3,
  infiniteScroll = false,
  readyForPagination = true,
  scrollContainerRefCurrent = null,
  showEndOfList = false,
  endOfListText = "End of list",
  variant = "default",
  className = "",
  themeKey = "",
  classNames = {},
}) => {
  const paginationThemeKey = themeKey || variant;
  const themeClassNames = useTemplateComponentClassNames(
    "pagination",
    paginationThemeKey,
  );
  const resolvedClassNames = {
    root: mergeClassNames(themeClassNames.root, classNames.root),
    infinite: mergeClassNames(themeClassNames.infinite, classNames.infinite),
    controls: mergeClassNames(themeClassNames.controls, classNames.controls),
    button: mergeClassNames(themeClassNames.button, classNames.button),
    activeButton: mergeClassNames(
      themeClassNames.activeButton,
      classNames.activeButton,
    ),
    ellipsis: mergeClassNames(themeClassNames.ellipsis, classNames.ellipsis),
    limit: mergeClassNames(themeClassNames.limit, classNames.limit),
    limitLabel: mergeClassNames(themeClassNames.limitLabel, classNames.limitLabel),
    limitSelect: mergeClassNames(
      themeClassNames.limitSelect,
      classNames.limitSelect,
    ),
    viewMore: mergeClassNames(themeClassNames.viewMore, classNames.viewMore),
    status: mergeClassNames(themeClassNames.status, classNames.status),
    endOfList: mergeClassNames(themeClassNames.endOfList, classNames.endOfList),
  };
  const safeLimit = Math.max(1, limit || 1);
  const maxPage = useMemo(
    () => Math.max(1, Math.ceil(total / safeLimit)),
    [total, safeLimit]
  );
  const totalPages = Math.ceil(total / safeLimit);
  const hasMore = page < totalPages;

  const [manualLoadMode, setManualLoadMode] = useState(false);
  const [scrollLoadCount, setScrollLoadCount] = useState(0);

  const loadMoreRef = useRef(null);
  const prevPageRef = useRef(page);

  const getPaginationRange = useCallback((current, totalPages, delta = 1) => {
    const range = [];
    const alwaysShowFirst = 1;
    const alwaysShowLast = totalPages;
    const start = Math.max(2, current - delta);
    const end = Math.min(totalPages - 1, current + delta);

    range.push(alwaysShowFirst);
    if (start > 2) range.push("...");
    for (let i = start; i <= end; i++) range.push(i);
    if (end < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(alwaysShowLast);
    return range;
  }, []);

  const showMore = () => {
    if (!isLoading && !isFetching && hasMore) {
      handlePageClick(page + 1);
    }
    setScrollLoadCount(0);
    setManualLoadMode(false);
  };

  const handlePageClick = useCallback(
    (nextPage) => {
      if (typeof onPageClick !== "function") return;
      if (!isFetching && !isLoading) onPageClick(nextPage);
    },
    [isFetching, isLoading, onPageClick]
  );

  // Intersection Observer for auto load
  useEffect(() => {
    if (!infiniteScroll) return;
    if (!readyForPagination) return;
    if (manualLoadMode || isLoading || isFetching || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && !isFetching && hasMore) {
          handlePageClick(page + 1);
        }
      },
      {
        root: scrollContainerRefCurrent || null,
        rootMargin: "100px",
        threshold: 0,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [
    infiniteScroll,
    manualLoadMode,
    isLoading,
    isFetching,
    hasMore,
    page,
    handlePageClick,
    scrollContainerRefCurrent,
  ]);

  // Track auto-load count
  useEffect(() => {
    if (!infiniteScroll || !enableManualMode) return;
    if (!readyForPagination) return;

    if (!isLoading && page > prevPageRef.current) {
      setScrollLoadCount((prev) => {
        const next = prev + 1;
        if (next >= autoLoadThreshold) {
          setManualLoadMode(true);
        }
        return next;
      });
      prevPageRef.current = page;
    }
  }, [infiniteScroll, page, isLoading, enableManualMode, autoLoadThreshold]);

  useEffect(() => {
    setManualLoadMode(false);
    setScrollLoadCount(0);
    prevPageRef.current = page;
  }, [total]);

  // Early returns
  if (
    (!infiniteScroll && !alwayShowPagination && total <= safeLimit) ||
    (infiniteScroll && total === 0)
  ) {
    return null;
  }

  const paginationClassName = [
    "pagination",
    infiniteScroll ? "pagination--infinite" : "",
    resolvedClassNames.root,
    infiniteScroll ? resolvedClassNames.infinite : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return !infiniteScroll ? (
    <div className={paginationClassName}>
      <div
        className={mergeClassNames(
          "pagination-controls numbered",
          resolvedClassNames.controls,
        )}
      >
        <button
          onClick={() => handlePageClick(Math.max(page - 3, 1))}
          disabled={page <= 3 || isFetching || isLoading}
          className={resolvedClassNames.button}
        >
          &laquo;
        </button>
        <button
          onClick={() => handlePageClick(Math.max(page - 1, 1))}
          disabled={page <= 1 || isFetching || isLoading}
          className={resolvedClassNames.button}
        >
          &lsaquo;
        </button>

        {getPaginationRange(page, maxPage).map((toPage, index) =>
          toPage === "..." ? (
            <span
              key={`ellipsis-${index}-${page}`}
              className={mergeClassNames(
                "pagination-ellipsis",
                resolvedClassNames.ellipsis,
              )}
            >
              …
            </span>
          ) : (
            <button
              key={toPage}
              onClick={() => handlePageClick(toPage)}
              className={mergeClassNames(
                resolvedClassNames.button,
                page === toPage ? "active" : "",
                page === toPage ? resolvedClassNames.activeButton : "",
              )}
              disabled={isFetching || isLoading}
            >
              {toPage}
            </button>
          )
        )}

        <button
          onClick={() => handlePageClick(Math.min(page + 1, maxPage))}
          disabled={page >= maxPage || isFetching || isLoading}
          className={resolvedClassNames.button}
        >
          &rsaquo;
        </button>
        <button
          onClick={() => handlePageClick(Math.min(page + 3, maxPage))}
          disabled={page >= maxPage - 2 || isFetching || isLoading}
          className={resolvedClassNames.button}
        >
          &raquo;
        </button>
      </div>

      {changeLimit && (
        <div
          className={mergeClassNames(
            "pagination-limit",
            resolvedClassNames.limit,
          )}
        >
          <label
            htmlFor="pagination-limit"
            className={resolvedClassNames.limitLabel}
          >
            Rows per page:
          </label>
          <select
            id="pagination-limit"
            value={limit}
            onChange={(e) => onLimitChange?.(Number(e.target.value))}
            disabled={isFetching || isLoading}
            className={resolvedClassNames.limitSelect}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  ) : (
    <div className={paginationClassName}>
      {enableManualMode && manualLoadMode && !isLoading && hasMore && (
        <button
          onClick={showMore}
          className={mergeClassNames("viewmore", resolvedClassNames.viewMore)}
          aria-label="Load more items"
        >
          View More
        </button>
      )}

      <div
        ref={
          !manualLoadMode && hasMore && readyForPagination ? loadMoreRef : null
        }
        className={mergeClassNames(
          "pagination-status",
          resolvedClassNames.status,
        )}
      >
        {(isLoading || isFetching) && <Spinner type="small" />}

        {!hasMore && !(isLoading || isFetching) && showEndOfList && (
          <span
            className={mergeClassNames(
              "end-of-list",
              resolvedClassNames.endOfList,
            )}
          >
            {endOfListText}
          </span>
        )}
      </div>
    </div>
  );
};

export default Pagination;
